/** Device-local fallback for an already validated sign-in, never a new login. */
const PREFIX = 'luma:offline-session:v1:';
export const OFFLINE_SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Metadata can outlive its generic cache TTL only while this identity holds
 * a server-issued audio lease. This never extends the audio lease itself. */
export function hasCurrentOfflineLease(userId: string, householdId?: string): boolean {
  try {
    const entries = JSON.parse(localStorage.getItem('luma:approved-cache:v1:' + userId) || '[]');
    return Array.isArray(entries) && entries.some((entry) => (!householdId || entry.householdId === householdId)
      && Number.isFinite(Date.parse(entry.confirmedAt)) && Date.parse(entry.confirmedAt) <= Date.now()
      && Number.isFinite(Date.parse(entry.accessExpiresAt)) && Date.now() < Date.parse(entry.accessExpiresAt));
  } catch { return false; }
}
type SnapshotKind = 'user' | 'family';
export interface SessionIdentity { userId: string; token: string }

export function sessionIdentity(): SessionIdentity | null {
  const userId = localStorage.getItem('user_id');
  const token = localStorage.getItem('auth_token');
  return userId && token ? { userId, token } : null;
}
export function isCurrentSession(identity: SessionIdentity): boolean {
  const current = sessionIdentity();
  return current?.userId === identity.userId && current.token === identity.token;
}
async function fingerprint(identity: SessionIdentity): Promise<string> {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(identity.token));
  return Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
export async function saveOfflineSnapshot<T>(kind: SnapshotKind, data: T, identity = sessionIdentity()): Promise<void> {
  if (!identity) return;
  try {
    const tokenHash = await fingerprint(identity);
    if (!isCurrentSession(identity)) return;
    localStorage.setItem(PREFIX + kind + ':' + identity.userId, JSON.stringify({ version: 1, tokenHash, savedAt: Date.now(), data }));
  } catch { /* Storage or Web Crypto unavailable: online use still works. */ }
}
export async function readOfflineSnapshot<T>(kind: SnapshotKind, identity = sessionIdentity()): Promise<T | null> {
  if (!identity) return null;
  try {
    const entry = JSON.parse(localStorage.getItem(PREFIX + kind + ':' + identity.userId) || 'null');
    const age = Date.now() - entry?.savedAt;
    if (!entry || entry.version !== 1 || !Number.isFinite(age) || age < 0 || (age > OFFLINE_SESSION_MAX_AGE_MS && !hasCurrentOfflineLease(identity.userId))
      || entry.tokenHash !== await fingerprint(identity) || !isCurrentSession(identity)) return null;
    return entry.data ?? null;
  } catch { return null; }
}
export function removeOfflineFamily(userId: string): void {
  localStorage.removeItem(PREFIX + 'family:' + userId);
}

/** Clear private device state before sign-out/account replacement. Public art remains. */
export async function clearPrivateOfflineData(): Promise<void> {
  const prefixes = [PREFIX, 'luma-measurement-outbox-', 'luma:approved-cache:', 'luma-household-cache-epochs-', 'luma:bedtime-context:', 'luma:story-mode-session:', 'luma:playback-progress:'];
  for (const storage of [localStorage, sessionStorage]) {
    Object.keys(storage).forEach((key) => { if (prefixes.some((prefix) => key.startsWith(prefix))) storage.removeItem(key); });
  }
  navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_HOUSEHOLD_CACHES' });
  if (typeof caches !== 'undefined') {
    try {
      await Promise.all((await caches.keys()).filter((key) => key.startsWith('luma-approved-audio-') || key.startsWith('luma-renders-api-')).map((key) => caches.delete(key)));
    } catch { /* A disabled Cache API must not prevent sign-out. */ }
  }
}
